import { useState, type FormEvent } from 'react';
import { useAccount } from '../components/AccountLayout';
import { KeyReveal } from '../components/RpcUi';
import { chainName, RPC_CHAINS } from '../lib/chains';
import { count, formatDate } from '../lib/money';
import { activeKeys, rpcApi, type KeyInput, type RpcKey } from '../lib/rpc-client';
import { SaasError } from '../lib/saas-client';

/** The limits form's state: strings for the number inputs so empty means
 *  "no limit of its own". */
interface LimitsDraft {
  rps: string;
  allChains: boolean;
  chains: number[];
  origins: string;
  proofs: boolean;
  maxBlocks: string;
  maxJobs: string;
}

const EMPTY_DRAFT: LimitsDraft = {
  rps: '',
  allChains: true,
  chains: RPC_CHAINS.map((c) => c.id),
  origins: '',
  proofs: true,
  maxBlocks: '',
  maxJobs: '',
};

function draftOf(k: RpcKey): LimitsDraft {
  return {
    rps: k.requests_per_second?.toString() ?? '',
    allChains: k.chains == null,
    chains: k.chains ?? RPC_CHAINS.map((c) => c.id),
    origins: (k.allowed_origins ?? []).join('\n'),
    proofs: k.proofs_enabled,
    maxBlocks: k.max_blocks_per_job?.toString() ?? '',
    maxJobs: k.max_open_jobs?.toString() ?? '',
  };
}

function num(s: string): number | null | 'bad' {
  const t = s.trim();
  if (!t) return null;
  if (!/^\d+$/.test(t)) return 'bad';
  return Number(t);
}

/** The draft as a KeyInput, or a message saying what's wrong. */
function toInput(d: LimitsDraft): KeyInput | string {
  const rps = num(d.rps);
  const maxBlocks = num(d.maxBlocks);
  const maxJobs = num(d.maxJobs);
  if (rps === 'bad' || maxBlocks === 'bad' || maxJobs === 'bad') {
    return 'Limits must be whole numbers, or empty for no limit of their own.';
  }
  if (!d.allChains && d.chains.length === 0) return 'Pick at least one chain, or allow all chains.';
  const origins = d.origins
    .split(/[\s,]+/)
    .map((o) => o.trim())
    .filter(Boolean);
  return {
    requests_per_second: rps,
    chains: d.allChains ? null : [...d.chains].sort((a, b) => a - b),
    allowed_origins: origins.length ? origins : null,
    proofs_enabled: d.proofs,
    max_blocks_per_job: maxBlocks,
    max_open_jobs: maxJobs,
  };
}

/** Only the fields that differ from the key, so PATCH leaves the rest alone. */
function changes(k: RpcKey, input: KeyInput, name: string): KeyInput {
  const out: KeyInput = {};
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  if (name.trim() && name.trim() !== k.name) out.name = name.trim();
  if (!same(input.requests_per_second, k.requests_per_second)) out.requests_per_second = input.requests_per_second;
  if (!same(input.chains, k.chains)) out.chains = input.chains;
  if (!same(input.allowed_origins, k.allowed_origins)) out.allowed_origins = input.allowed_origins;
  if (input.proofs_enabled !== k.proofs_enabled) out.proofs_enabled = input.proofs_enabled;
  if (!same(input.max_blocks_per_job, k.max_blocks_per_job)) out.max_blocks_per_job = input.max_blocks_per_job;
  if (!same(input.max_open_jobs, k.max_open_jobs)) out.max_open_jobs = input.max_open_jobs;
  return out;
}

function limitsSummary(k: RpcKey): string {
  const parts: string[] = [];
  if (k.requests_per_second != null) parts.push(`${count(k.requests_per_second)} req/s`);
  parts.push(
    k.chains == null
      ? 'all chains'
      : k.chains.length <= 2
        ? k.chains.map(chainName).join(', ')
        : `${k.chains.length} chains`,
  );
  if (k.allowed_origins)
    parts.push(`${k.allowed_origins.length} origin${k.allowed_origins.length === 1 ? '' : 's'}`);
  if (!k.proofs_enabled) parts.push('no proofs');
  if (k.max_blocks_per_job != null) parts.push(`${count(k.max_blocks_per_job)} blocks/job`);
  if (k.max_open_jobs != null) parts.push(`${count(k.max_open_jobs)} open jobs`);
  return parts.join(' · ');
}

function LimitsFields({
  draft,
  onChange,
  planRps,
  idPrefix,
}: {
  draft: LimitsDraft;
  onChange: (d: LimitsDraft) => void;
  planRps: number | null;
  idPrefix: string;
}) {
  const set = (patch: Partial<LimitsDraft>) => onChange({ ...draft, ...patch });
  return (
    <div className="rpc-limits">
      <label className="rpc-field">
        <span>Requests per second</span>
        <input
          inputMode="numeric"
          value={draft.rps}
          placeholder={planRps ? `plan limit, ${planRps}` : 'plan limit'}
          onChange={(e) => set({ rps: e.target.value })}
          data-testid={`${idPrefix}-rps`}
        />
        <small>Lower than your plan's limit, or empty to use the plan's.</small>
      </label>
      <fieldset className="rpc-field rpc-chains">
        <legend>Chains</legend>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={draft.allChains}
            onChange={(e) => set({ allChains: e.target.checked })}
            data-testid={`${idPrefix}-all-chains`}
          />
          <span>All chains</span>
        </label>
        {!draft.allChains && (
          <div className="rpc-chain-grid">
            {RPC_CHAINS.map((c) => (
              <label key={c.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={draft.chains.includes(c.id)}
                  onChange={(e) =>
                    set({
                      chains: e.target.checked
                        ? [...draft.chains, c.id]
                        : draft.chains.filter((x) => x !== c.id),
                    })
                  }
                  data-testid={`${idPrefix}-chain-${c.id}`}
                />
                <span>{c.name}</span>
              </label>
            ))}
          </div>
        )}
      </fieldset>
      <label className="rpc-field rpc-field--wide">
        <span>Allowed origins</span>
        <textarea
          rows={2}
          value={draft.origins}
          placeholder="https://app.example.com"
          onChange={(e) => set({ origins: e.target.value })}
          data-testid={`${idPrefix}-origins`}
        />
        <small>
          One per line. Browsers on other sites are refused. Empty allows any origin. This
          doesn't stop a server that has the key, so keep keys out of public code.
        </small>
      </label>
      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={draft.proofs}
          onChange={(e) => set({ proofs: e.target.checked })}
          data-testid={`${idPrefix}-proofs`}
        />
        <span>Allow proof requests (charged per proven block)</span>
      </label>
      <div className="rpc-limits-row">
        <label className="rpc-field">
          <span>Most blocks per proof job</span>
          <input
            inputMode="numeric"
            value={draft.maxBlocks}
            placeholder="default"
            onChange={(e) => set({ maxBlocks: e.target.value })}
            data-testid={`${idPrefix}-max-blocks`}
          />
        </label>
        <label className="rpc-field">
          <span>Most open proof jobs</span>
          <input
            inputMode="numeric"
            value={draft.maxJobs}
            placeholder="default"
            onChange={(e) => set({ maxJobs: e.target.value })}
            data-testid={`${idPrefix}-max-jobs`}
          />
        </label>
      </div>
    </div>
  );
}

function KeyRow({
  k,
  planRps,
  busy,
  onRotate,
  onRevoke,
  onSaved,
}: {
  k: RpcKey;
  planRps: number | null;
  busy: boolean;
  onRotate: (k: RpcKey) => void;
  onRevoke: (k: RpcKey) => void;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(k.name);
  const [draft, setDraft] = useState<LimitsDraft>(() => draftOf(k));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setName(k.name);
    setDraft(draftOf(k));
    setError(null);
    setEditing(true);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const input = toInput(draft);
    if (typeof input === 'string') {
      setError(input);
      return;
    }
    const diff = changes(k, input, name);
    if (Object.keys(diff).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await rpcApi.updateKey(k.key_id, diff);
      await onSaved();
      setEditing(false);
    } catch (err) {
      setError(err instanceof SaasError ? err.message : 'Could not save the key.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <li className="rpc-key" data-testid={`key-row-${k.key_id}`}>
      <div className="rpc-key-main">
        <div className="rpc-key-id">
          <span className="rpc-key-name">{k.name}</span>
          <code className="rpc-key-prefix">{k.prefix}…</code>
        </div>
        <div className="rpc-key-meta">
          Created {formatDate(k.created_at)} · {limitsSummary(k)}
        </div>
      </div>
      <div className="rpc-key-actions">
        <button className="btn btn-sm" type="button" onClick={startEdit} disabled={busy || editing}>
          Edit
        </button>
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => onRotate(k)}
          disabled={busy}
          data-testid={`rotate-${k.key_id}`}
        >
          Rotate
        </button>
        <button
          className="btn btn-sm rpc-danger"
          type="button"
          onClick={() => onRevoke(k)}
          disabled={busy}
          data-testid={`revoke-${k.key_id}`}
        >
          Revoke
        </button>
      </div>
      {editing && (
        <form className="rpc-key-edit" onSubmit={onSave} data-testid={`edit-${k.key_id}`}>
          <label className="rpc-field">
            <span>Name</span>
            <input value={name} maxLength={64} onChange={(e) => setName(e.target.value)} />
          </label>
          <LimitsFields draft={draft} onChange={setDraft} planRps={planRps} idPrefix={`edit-${k.key_id}`} />
          {error && <p className="form-error">{error}</p>}
          <div className="rpc-form-actions">
            <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-sm btn-ghost" type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </li>
  );
}

export function KeysPanel() {
  const { keys: listed, keysError, reloadKeys, summary, lastMinted, setLastMinted } = useAccount();
  const keys = listed && activeKeys(listed);
  const [name, setName] = useState('');
  const [showLimits, setShowLimits] = useState(false);
  const [draft, setDraft] = useState<LimitsDraft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealTitle, setRevealTitle] = useState('Your new key');
  const planRps = summary?.plan.requests_per_second ?? null;

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    let input: KeyInput = { name: name.trim() || undefined };
    if (showLimits) {
      const limits = toInput(draft);
      if (typeof limits === 'string') {
        setError(limits);
        return;
      }
      input = { ...input, ...limits };
    }
    setBusy(true);
    setError(null);
    try {
      setRevealTitle('Your new key');
      setLastMinted(await rpcApi.createKey(input));
      setName('');
      setDraft(EMPTY_DRAFT);
      setShowLimits(false);
      await reloadKeys();
    } catch (err) {
      setError(err instanceof SaasError ? err.message : 'Could not create the key.');
    } finally {
      setBusy(false);
    }
  }

  async function onRotate(k: RpcKey) {
    if (
      !confirm(
        `Rotate "${k.name}"? You get a new key with the same name and limits, and the old one stops working within seconds.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      setRevealTitle('Your rotated key');
      setLastMinted(await rpcApi.rotateKey(k.key_id));
      await reloadKeys();
    } catch (err) {
      setError(err instanceof SaasError ? err.message : 'Could not rotate the key.');
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(k: RpcKey) {
    if (!confirm(`Revoke "${k.name}"? Requests with it get HTTP 401 within seconds. This can't be undone.`))
      return;
    setBusy(true);
    setError(null);
    try {
      await rpcApi.revokeKey(k.key_id);
      if (lastMinted?.key_id === k.key_id) setLastMinted(null);
      await reloadKeys();
    } catch (err) {
      setError(err instanceof SaasError ? err.message : 'Could not revoke the key.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {lastMinted && (
        <KeyReveal minted={lastMinted} title={revealTitle} onDismiss={() => setLastMinted(null)} />
      )}

      <section className="panel">
        <div className="panel-eyebrow">Create a key</div>
        <p className="panel-description">
          One key works on all seven chains. Use a separate key for each app or server, so
          you can see its usage and revoke it alone.
        </p>
        <form onSubmit={onCreate} data-testid="create-key-form">
          <div className="rpc-create-row">
            <input
              className="rpc-input"
              type="text"
              placeholder="Name, e.g. production-indexer"
              maxLength={64}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              data-testid="new-key-name"
            />
            <button className="btn btn-primary" type="submit" disabled={busy} data-testid="create-key">
              {busy ? 'Working…' : 'Create key'}
            </button>
          </div>
          <button
            type="button"
            className="btn-link rpc-limits-toggle"
            onClick={() => setShowLimits((v) => !v)}
            aria-expanded={showLimits}
            data-testid="toggle-limits"
          >
            {showLimits ? 'Hide limits' : 'Set limits for this key'}
          </button>
          {showLimits && (
            <LimitsFields draft={draft} onChange={setDraft} planRps={planRps} idPrefix="new" />
          )}
        </form>
        {error && <p className="form-error">{error}</p>}
      </section>

      <section className="panel">
        <div className="panel-eyebrow">
          Your keys{keys && keys.length > 0 && <span className="muted"> · {keys.length} active</span>}
        </div>
        {keysError && <p className="form-error">{keysError}</p>}
        {keys === null ? (
          <p className="muted">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="muted" data-testid="no-keys">
            No keys yet. Create one above.
          </p>
        ) : (
          <ul className="rpc-key-list" data-testid="key-list">
            {keys.map((k) => (
              <KeyRow
                key={k.key_id}
                k={k}
                planRps={planRps}
                busy={busy}
                onRotate={onRotate}
                onRevoke={onRevoke}
                onSaved={reloadKeys}
              />
            ))}
          </ul>
        )}
        <p className="muted rpc-small">
          Send a key in the URL (<code>/v1/rpc/{'{chain}'}/{'{key}'}</code>) or in an{' '}
          <code>Authorization: Bearer</code> or <code>X-API-Key</code> header. Changes and
          revokes reach the RPC within seconds.
        </p>
      </section>
    </>
  );
}
